import { createElement } from 'react';
import {
  ActivationPackWithTarget,
  GeneratedAsset,
  ASSET_TYPE_INFO,
} from '@/types/activation/activation-pack';
import { LookbookDesign } from '@/types/activation/lookbook';
import { resolveLookbookBrand } from '@/components/activation/lookbook/lookbookBrand';
import { LookbookDocument } from '@/components/activation/lookbook/LookbookDocument';
import { LookbookPageFrame, LOOKBOOK_PAGE_W, LOOKBOOK_PAGE_H } from '@/components/activation/lookbook/LookbookPageFrame';

const sanitizeFilename = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'genie';

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Verify a logo loads cleanly under CORS before it's ever rendered into the
 * page. A logo host that doesn't send `Access-Control-Allow-Origin` taints
 * html2canvas's output canvas, and `canvas.toDataURL()` then throws a
 * SecurityError that aborts the whole export — same failure mode fixed for
 * the QR download in QRCodeModal and worked around in
 * AssetGeneratorService.loadImage. Resolves the URL only on a clean load;
 * callers should swap it for `null` otherwise so the brand mark falls back
 * to its initial-tile placeholder instead of a doomed `<img>`.
 */
const preloadLogo = (src: string | null): Promise<string | null> =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(src);
    img.onerror = () => resolve(null);
    img.src = src;
    setTimeout(() => resolve(null), 2500);
  });

/**
 * Render the lookbook React document offscreen, rasterize each A4 page with
 * html2canvas, and assemble a jsPDF. Keeps the in-app gallery and the PDF
 * pixel-identical (same components). QR-bearing pages export as PNG so codes
 * stay crisp/scannable; the cover/closing use JPEG to keep the file lean.
 */
export async function generateLookbookPdf(pack: ActivationPackWithTarget): Promise<GeneratedAsset> {
  const brand = resolveLookbookBrand(pack);

  const [{ default: html2canvas }, { default: jsPDF }, { createRoot }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
    import('react-dom/client'),
  ]);

  // Offscreen mount — laid out at native size but parked far off-screen.
  const container = document.createElement('div');
  container.style.cssText =
    `position:absolute; left:-10000px; top:0; width:${LOOKBOOK_PAGE_W}px; background:#ffffff;`;
  document.body.appendChild(container);
  const root = createRoot(container);

  try {
    // Verify the logo before it's ever painted — see preloadLogo.
    const safeBrand = { ...brand, logoUrl: await preloadLogo(brand.logoUrl) };
    root.render(createElement(LookbookDocument, { brand: safeBrand }));

    // Let fonts and the QR canvases settle before capture.
    if (document.fonts?.ready) await document.fonts.ready;
    await wait(450);

    const pages = Array.from(container.querySelectorAll<HTMLElement>('[data-lookbook-page]'));

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const PT_W = 595.28;
    const PT_H = 841.89;

    for (let i = 0; i < pages.length; i++) {
      const node = pages[i];
      const isDesign = node.dataset.kind === 'design';
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: LOOKBOOK_PAGE_W,
        height: LOOKBOOK_PAGE_H,
        windowWidth: LOOKBOOK_PAGE_W,
        windowHeight: LOOKBOOK_PAGE_H,
      });
      const data = isDesign ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.9);
      if (i > 0) pdf.addPage();
      pdf.addImage(data, isDesign ? 'PNG' : 'JPEG', 0, 0, PT_W, PT_H, undefined, 'FAST');
    }

    return {
      type: 'lookbook_pdf',
      label: ASSET_TYPE_INFO.lookbook_pdf.label,
      description: ASSET_TYPE_INFO.lookbook_pdf.description,
      data: pdf.output('blob'),
      filename: `${sanitizeFilename(pack.name)}-lookbook.pdf`,
      mimeType: 'application/pdf',
    };
  } finally {
    root.unmount();
    container.remove();
  }
}

/**
 * Render just ONE lookbook placement design to a single-page A4 PDF blob —
 * used when someone selects individual designs (not the whole lookbook) to
 * add to their print order. Same rasterization path as the full document.
 */
export async function generateLookbookDesignPdf(
  pack: ActivationPackWithTarget,
  design: LookbookDesign,
): Promise<Blob> {
  const brand = resolveLookbookBrand(pack);

  const [{ default: html2canvas }, { default: jsPDF }, { createRoot }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
    import('react-dom/client'),
  ]);

  const container = document.createElement('div');
  container.style.cssText =
    `position:absolute; left:-10000px; top:0; width:${LOOKBOOK_PAGE_W}px; background:#ffffff;`;
  document.body.appendChild(container);
  const root = createRoot(container);

  try {
    const safeBrand = { ...brand, logoUrl: await preloadLogo(brand.logoUrl) };
    root.render(createElement(LookbookPageFrame, { brand: safeBrand, design, index: 1, total: 1 }));

    if (document.fonts?.ready) await document.fonts.ready;
    await wait(350);

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: LOOKBOOK_PAGE_W,
      height: LOOKBOOK_PAGE_H,
      windowWidth: LOOKBOOK_PAGE_W,
      windowHeight: LOOKBOOK_PAGE_H,
    });

    const PT_W = 595.28;
    const PT_H = 841.89;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, PT_W, PT_H, undefined, 'FAST');
    return pdf.output('blob');
  } finally {
    root.unmount();
    container.remove();
  }
}
